import { getAuthProviderFetchJson } from './Auth';
import { superAdminApiServer } from './config';

export type PartnerStatus = 'active' | 'deactivated';

export const partnerStatusChoices: { id: PartnerStatus; name: string }[] = [
  { id: 'active', name: 'Active' },
  { id: 'deactivated', name: 'Deactivated' }
];

export interface PartnerConfigRecord {
  id: string;
  uuid: string;
  partnerUuid: string;
  domain: string | null;
  cssUrl: string | null;
  payrollUrl: string | null;
  payrollUrlAuthStrategy: string | null;
  extraOptions: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerRecord {
  id: string;
  uuid: string;
  name: string;
  status: PartnerStatus;
  createdAt: string;
  updatedAt: string;
  config?: PartnerConfigRecord | null;
  hasConfig?: boolean;
}

export interface PartnerConfigBody {
  domain: string | null;
  cssUrl: string | null;
  payrollUrl: string | null;
  payrollUrlAuthStrategy: string | null;
  extraOptions: Record<string, unknown> | null;
}

const emptyToNull = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
};

export const partnerBody = (data: Record<string, unknown>) => ({ name: String(data.name ?? '').trim(), status: data.status as PartnerStatus | undefined });

export const partnerConfigBody = (data: Record<string, unknown>): PartnerConfigBody => {
  const { extraOptions } = data;
  return {
    domain: emptyToNull(data.domain),
    cssUrl: emptyToNull(data.cssUrl),
    payrollUrl: emptyToNull(data.payrollUrl),
    payrollUrlAuthStrategy: emptyToNull(data.payrollUrlAuthStrategy),
    extraOptions: typeof extraOptions === 'object' && extraOptions !== null && !Array.isArray(extraOptions) ? (extraOptions as Record<string, unknown>) : null
  };
};

export const changedConfigFields = (body: PartnerConfigBody, original: PartnerConfigRecord): Partial<PartnerConfigBody> => {
  const keys = Object.keys(body) as (keyof PartnerConfigBody)[];
  return Object.fromEntries(keys.filter((key) => JSON.stringify(body[key]) !== JSON.stringify(original[key] ?? null)).map((key) => [key, body[key]]));
};

const configUrl = (partnerUuid: string) => `${superAdminApiServer}/partners/${encodeURIComponent(partnerUuid)}/config`;

export const savePartnerConfig = async (partnerUuid: string, body: Partial<PartnerConfigBody>, mode: 'create' | 'edit'): Promise<PartnerConfigRecord> => {
  const { json } = await getAuthProviderFetchJson(configUrl(partnerUuid), { method: mode === 'create' ? 'POST' : 'PUT', body: JSON.stringify(body) });
  return json as PartnerConfigRecord;
};

export const deletePartnerConfig = async (partnerUuid: string): Promise<void> => {
  await getAuthProviderFetchJson(configUrl(partnerUuid), { method: 'DELETE' });
};

export const errorMessage = (error: unknown): string => {
  const body = (error as { body?: { message?: unknown } } | null)?.body;
  if (Array.isArray(body?.message)) return body!.message.join('; ');
  if (typeof body?.message === 'string' && body.message) return body.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Request failed';
};
